import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, RotateCcw, RotateCw } from 'lucide-react';
import { IdCardsApi } from '@/services/id-cards.api';
import { Modal } from '@/components/ui/Modal';
import { Field, Input, Textarea } from '@/components/ui/Input';

/**
 * "Edit & Lock ID Card Data" — matches the real legacy screen field-for-
 * field (Student First/Last Name, DOB, Mobile, Guardian First/Last Name,
 * Guardian Mobile, Address, photo Rotate L/R, Cancel/Save Only/Save & Lock).
 * Students only — the legacy screen this is built from is student-specific.
 */
export function EditIdCardModal({ studentId, onClose }: { studentId: string | null; onClose: () => void }) {
  const qc = useQueryClient();
  const open = !!studentId;

  const { data, isFetching } = useQuery({
    queryKey: ['id-cards', 'edit', studentId],
    queryFn: () => IdCardsApi.getEditData(studentId!),
    enabled: open,
  });

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [mobile, setMobile] = useState('');
  const [guardianFirstName, setGuardianFirstName] = useState('');
  const [guardianLastName, setGuardianLastName] = useState('');
  const [guardianMobile, setGuardianMobile] = useState('');
  const [address, setAddress] = useState('');
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setFirstName(data.firstName ?? '');
    setLastName(data.lastName ?? '');
    setDob(data.dob ?? '');
    setMobile(data.mobile ?? '');
    setGuardianFirstName(data.guardianFirstName ?? '');
    setGuardianLastName(data.guardianLastName ?? '');
    setGuardianMobile(data.guardianMobile ?? '');
    setAddress(data.address ?? '');
    setPhotoUrl(data.photoUrl);
  }, [data]);

  const rotate = useMutation({
    mutationFn: (direction: 'left' | 'right') => IdCardsApi.rotatePhoto(studentId!, 'students', direction),
    onSuccess: (r) => setPhotoUrl(r.photoUrl),
    onError: (e: any) => setError(e?.response?.data?.error?.message ?? 'Could not rotate the photo.'),
  });

  const save = useMutation({
    mutationFn: (lock: boolean) =>
      IdCardsApi.update(studentId!, {
        firstName,
        lastName: lastName || undefined,
        dob: dob || undefined,
        mobile: mobile || undefined,
        guardianFirstName: guardianFirstName || undefined,
        guardianLastName: guardianLastName || undefined,
        guardianMobile: guardianMobile || undefined,
        address: address || undefined,
        lock,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['id-cards', 'verification'] });
      qc.invalidateQueries({ queryKey: ['id-cards', 'preview', studentId] });
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error?.message ?? 'Could not save.'),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Edit & Lock ID Card Data"
      size="xl"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose} disabled={save.isPending}>
            Cancel
          </button>
          <button className="btn-secondary" onClick={() => save.mutate(false)} disabled={save.isPending || !firstName.trim()}>
            {save.isPending && save.variables === false ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
            Save Only
          </button>
          <button
            className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            onClick={() => save.mutate(true)}
            disabled={save.isPending || !firstName.trim()}
          >
            {save.isPending && save.variables === true ? <Loader2 className="mr-1.5 inline h-4 w-4 animate-spin" /> : null}
            Save &amp; Lock
          </button>
        </>
      }
    >
      {isFetching || !data ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <strong>Warning:</strong> Once you verify and lock the data, you will not be able to edit it again.
          </div>
          {error && (
            <div className="flex items-center justify-between rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
                &times;
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 gap-6 md:grid-cols-[160px_1fr]">
            <div>
              <div className="mb-2 text-sm font-semibold text-slate-700">Student Photo</div>
              {photoUrl ? (
                <img src={photoUrl} alt="Student" className="h-32 w-32 rounded-md border border-slate-300 object-cover" />
              ) : (
                <div className="flex h-32 w-32 items-center justify-center rounded-md border border-dashed border-slate-300 text-xs text-slate-400">
                  No photo
                </div>
              )}
              {photoUrl && (
                <div className="mt-2 flex gap-2">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    onClick={() => rotate.mutate('left')}
                    disabled={rotate.isPending}
                  >
                    <RotateCcw className="h-3.5 w-3.5" /> Rotate L
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                    onClick={() => rotate.mutate('right')}
                    disabled={rotate.isPending}
                  >
                    <RotateCw className="h-3.5 w-3.5" /> Rotate R
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Student First Name" required>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
              </Field>
              <Field label="Student Last Name">
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
              </Field>
              <Field label="Date of Birth">
                <Input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
              </Field>
              <Field label="Student Mobile">
                <Input value={mobile} onChange={(e) => setMobile(e.target.value)} />
              </Field>
              <Field label="Guardian First Name">
                <Input value={guardianFirstName} onChange={(e) => setGuardianFirstName(e.target.value)} />
              </Field>
              <Field label="Guardian Last Name">
                <Input value={guardianLastName} onChange={(e) => setGuardianLastName(e.target.value)} />
              </Field>
              <Field label="Guardian Mobile">
                <Input value={guardianMobile} onChange={(e) => setGuardianMobile(e.target.value)} />
              </Field>
              <Field label="Address" className="sm:col-span-2">
                <Textarea rows={3} value={address} onChange={(e) => setAddress(e.target.value)} />
              </Field>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
